import { useState, ChangeEvent, useEffect } from "react";

// MUI JSX elements
import {
  Box,
  Card,
  Table,
  TableBody,
  TableHead,
  TablePagination,
} from "@mui/material";

// Custom JSX elements
import { TableContainerStyled, PaginationWrapperStyled, } from './dataGrid.styled';
import DatagridHeader, { Order, IHeadCell } from "./dataGridHeader/datagridHeader";
import DatagridRows from "./dataGridRows/datagridRows";
import { Skeleton, TableCell, TableRow, TextField } from "@mui/material";

// Custom Functions / Data
import { DEFAULT_ROWS_NUMBER } from "core/utils/constants";

// Types
import { IRowProps, ISelectedData } from "./dataGrid.type";
import { UserClaims } from "core/contexts/auth/auth";

interface TableRowsLoader {
  rowsNum: number;
  columnNum: number;
  headCells: IHeadCell[];
}

const TableRowsLoader = ({ rowsNum, columnNum, headCells }: TableRowsLoader) => {
  return (
    <>
      <TableHead>
        <TableRow key={`headers-row-0`}>
          {[...Array(columnNum)].map((_, columnIndex) => (
            <TableCell key={`header-cell-${columnIndex}`}>
              <Skeleton animation="wave" variant="text" sx={{width: '20px'}}/>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(rowsNum)].map((row, index) => (
          <TableRow key={`body-row-${index}`}>
            {[...Array(columnNum)].map((_, columnIndex) => (
              <TableCell key={`body-cell-${columnIndex}`}>
                <Skeleton animation="wave" variant="text" sx={{lineHeight: "53px"}} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </>
  );
};

interface DatagridProps {
  rowData: IRowProps[];
  headCells: IHeadCell[];
  visibleKeys?: string[]
  defaultSorterColumn?: string;
  onRowsPerPageChange?: (value: number) => void;
  maxWidths?: Record<string, number>;
  onRowClick?: any;
  showSelectionCheckbox?: boolean;
  showPagination?: boolean;
  onPageChange?: (pageNumber: number) => void;
  pageSize?: number;
  pageNumber?: number;
  totalData?: number;
  onSortChange?: (field: string | number | symbol, order: string | number) => void;
  sortField?: string;
  order?: Order;
  customDesign?: boolean;
  highlightedRowId?: string;
  setHighlightedRowId?: (rowId: string) => void;
  showCustomSort?: boolean;
  isFilterModalOpen?: boolean;
  setIsFilterModalOpen?: (openModel: boolean) => void;
  filterData?: any,
  setFilterData?: any,
  handleAssignedTo?: any;
  users?: UserClaims[];
  isPageLoading?: boolean;
  setPage?: (pageNum: number) => void;
  page: number;
  statusMapping?: { [key: string]: string };
  handleSetStatus?: (status: string, rowId: string) => void;
  handleRefresh?: any,
  onActionClick?:(rowId:string,type:string) => void;
}

const Datagrid = ({
  rowData,
  headCells,
  visibleKeys,
  onRowsPerPageChange,
  defaultSorterColumn = "firstName",
  maxWidths = {},
  onRowClick,
  showSelectionCheckbox = false,
  showPagination = false,
  pageSize = DEFAULT_ROWS_NUMBER,
  pageNumber = 0,
  totalData = 0,
  setPage,
  page,
  onPageChange,
  onSortChange,
  highlightedRowId = "",
  setHighlightedRowId,
  showCustomSort,
  isFilterModalOpen,
  setIsFilterModalOpen,
  filterData,
  setFilterData,
  handleAssignedTo,
  users,
  isPageLoading,
  statusMapping,
  handleSetStatus,
  handleRefresh,
  onActionClick
}: DatagridProps) => {

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>(defaultSorterColumn);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  if (!visibleKeys) {
    visibleKeys = headCells.map((header) => header.id);
  }



  const handleRequestSort = (
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    if (onSortChange) onSortChange(property, isAsc ? "desc" : "asc");
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rowData.map((n) => (n?.id ?? '') as string);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  
    if (setPage) {
      setPage(newPage);
    }
  };

  function handleChangeRowsPerPage(event: ChangeEvent<HTMLInputElement>) {
    if (setPage) {
      if (onRowsPerPageChange) {
        onRowsPerPageChange(parseInt(event.target.value, 10));
      }
      setRowsPerPage(parseInt(event.target.value, 10));
      
      setPage(0);
    }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainerStyled>
        <>{isPageLoading}</>
        {/* Data */}

        <Table aria-labelledby="tableTitle" size={"small"}>
          {/* { !isPageLoading ?  */}
              <>
                {/* Headers */}
                <DatagridHeader
                  headCells={headCells}
                  visibleKeys={visibleKeys}
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rowData.length}
                  onFilterClick={() => console.log("")}
                  showSelectionCheckbox={showSelectionCheckbox}
                />

                {/* Row Data */}
                <DatagridRows
                  headCells={headCells}
                  visibleRows = {rowData} 
                  visibleKeys = {visibleKeys}
                  page = {page}
                  rowsPerPage = {rowsPerPage}
                  totalRows = {rowData.length}
                  highlightedRowId = {highlightedRowId}
                  setHighlightedRowId = {setHighlightedRowId}
                  selected = {selected}
                  setSelected = {setSelected}
                  onRowClick = {onRowClick}
                  maxWidths = {maxWidths}
                  handleAssignedTo={handleAssignedTo || (() => undefined)}
                  users={users}
                  statusMapping={statusMapping}
                  handleSetStatus={handleSetStatus}
                  onActionClick={onActionClick}
                /> 
              </>
             {/* : */}
             {/* <TableRowsLoader rowsNum={rowsPerPage} columnNum={7} headCells={headCells}/> */}
          {/* } */}
        </Table>
      </TableContainerStyled>
      
      {/* MUI Table Pagination Widgets */}
      {showPagination && !!rowData.length && (
        <PaginationWrapperStyled>
          <TablePagination
            component='div'
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            count={totalData}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton={true}
            showLastButton={true}
          />
        </PaginationWrapperStyled>
      )}
    </Box>
  );
};


export default Datagrid;
